import React, { useState, useEffect,useRef } from 'react';
import { initializeApp } from 'firebase/app';
import { getFirestore, collection, addDoc, getDocs, doc, updateDoc, deleteDoc, getDoc, setDoc } from 'firebase/firestore';
import { getAuth, signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';
import { Menu, X, ChevronRight, Phone, Mail, MapPin, Check, Star, TrendingUp, Shield, Zap, Users, BarChart3, Clock, AlertCircle, ArrowRight, DollarSign, Globe, Lock, FileText, CheckCircle, Edit, Trash2, Plus, Eye, EyeOff, Sparkles, Award, Target, Rocket, MessageCircle, Send } from 'lucide-react';

// Firebase Configuration
const firebaseConfig = {
    apiKey: "AIzaSyBGcHmlce6U1rCPG-7OD3E45F0b3jeo8i8",
    authDomain: "zyppro-rms.firebaseapp.com",
    projectId: "zyppro-rms",
    storageBucket: "zyppro-rms.firebasestorage.app",
    messagingSenderId: "608367634317",
    appId: "1:608367634317:web:fd3e2e2b73673d60da3fc4",
    measurementId: "G-4VHF5X2V03"
};

const app = initializeApp(firebaseConfig);
const db = getFirestore(app);
const auth = getAuth(app);

const BILL_PRINTER_PRICE = 2000;

// --- RESTAURANT PACKAGES (Existing) ---
const DEFAULT_PACKAGES = [
    {
        id: 1,
        name: 'Basic',
        price: 499,
        qrTables: 10,
        type: 'restaurant',
        features: [
            'QR Tables Limit 10 Tables',
            'Simple Dashboard',
            'QR Menu Access',
            'Daily Sales Summary',
            'Auto Billing & GST Invoice',
            '1 GB Storage',
            '100 Menu Items',
            'Basic Inventory Management',
            'Real-time Order Updates',
        ],
        popular: false
    },
    {
        id: 2,
        name: 'Premium',
        price: 999,
        qrTables: 30,
        type: 'restaurant',
        features: [
            'QR Tables Limit 30 Tables',
            'Everything in Basic',
            'Full Analytics Dashboard',
            'Revenue & Sales Reports',
            'Profit Loss Dashboard',
            '5 GB Storage',
            '200 Menu Items',
            'Advanced Inventory Management',
            'Employee Management',
            'Push Notifications',
        ],
        popular: true
    },
    {
        id: 3,
        name: 'Advanced',
        price: 1999,
        qrTables: 60,
        type: 'restaurant',
        features: [
            'QR Tables Limit 60 Tables',
            'Everything in Premium',
            'AI-Powered Insights',
            'Fraud Detection System',
            'Unlimited Menu Items',
            '20 GB Storage',
            'Complete Employee Management',
            'WhatsApp-like Push Notifications',
            'Priority Support 24/7',
            'Unlimited Tables',
        ],
        popular: false
    }
];

// --- HOTEL PACKAGES (NEW) ---
const HOTEL_PACKAGES = [
    {
        id: 101,
        name: 'Lite',
        price: 299,
        rooms: 15,
        type: 'hotel',
        features: [
            'QR Room Ordering: Up to 15 Rooms',
            'Replaces Telephonic Orders',
            'Counter Staff Order Notifications (App/Web)',
            'Real-time WhatsApp-like Notifications (No Order Missed)',
            'Hotel Staff: Update Prep Time (Customer Sees Live Time)',
            'Unique QR Code to Each Room',
        ],
        popular: false
    },
    {
        id: 102,
        name: 'Standard',
        price: 499,
        rooms: 30,
        type: 'hotel',
        features: [
            'QR Room Ordering: Up to 30 Rooms',
            'Everything in Lite',
            'One-Click Google Review Popup on Delivery',
            'Detailed Analytics (Day/Week/Month Sales)',
            'Peak Hours & High-Selling Item Reports',
            'Create Manual Bill',
            'Real-time Order Updates (Ready & Delivered)',
        ],
        popular: true
    },
    {
        id: 103,
        name: 'Pro',
        price: 999,
        rooms: 60,
        type: 'hotel',
        features: [
            'QR Room Ordering: Up to 60 Rooms',
            'Everything in Standard',
            'Full Financial Analytics Dashboard',
            'Unlimited Menu Items',
            'Priority Support 24/7',
            'Dedicated Account Manager',
            'Unlimited Data Storage',
        ],
        popular: false
    }
];

// --- RESTAURANT FEATURES (For Features Page) ---
const RESTAURANT_FEATURES = [
    {
        title: '📱 QR Menu and Ordering System',
        features: [
            { icon: '📱', name: 'QR Code Ordering', desc: 'Customers scan & order directly from their phones using our QR scanner.' },
            { icon: '⏱️', name: 'Real-time Updates', desc: 'Live order status updates to customers.' },
            { icon: '🔔', name: 'Push Notifications', desc: 'WhatsApp-like notifications for order updates.' },
            { icon: '🍔', name: 'Digital Menu', desc: 'Beautiful digital menu with photos & descriptions.' }
        ]
    },
    {
        title: '💰 Billing Software & Finance',
        features: [
            { icon: '🧾', name: 'Auto Billing & GST Invoice', desc: 'Automatic bill generation with GST compliance.' },
            { icon: '📄', name: 'Invoice Generation', desc: 'Professional invoices with your branding.' },
            { icon: '📊', name: 'Profit/Loss Dashboard', desc: 'Clear view of daily, weekly, monthly profits.' },
            { icon: '🔒', name: 'Fraud Detection', desc: 'Monitor cashier billing for suspicious patterns.' }
        ]
    },
    {
        title: '📊 POS Analytics & Reports',
        features: [
            { icon: '📈', name: 'Sales Dashboard', desc: 'Real-time sales tracking & visualization.' },
            { icon: '🏆', name: 'Best Sellers', desc: 'Know your top-performing menu items.' },
            { icon: '⏰', name: 'Peak Hours Analysis', desc: 'Identify busy times for better planning.' },
            { icon: '💹', name: 'Revenue Reports', desc: 'Detailed revenue breakdown & trends.' }
        ]
    },
    {
        title: '👥 Operations Management Tools',
        features: [
            { icon: '👨‍💼', name: 'Employee Management', desc: 'Track staff performance, shifts & attendance.' },
            { icon: '📦', name: 'Inventory Management', desc: 'Real-time stock tracking & low-stock alerts.' },
            { icon: '🍽️', name: 'Digital Menu Management', desc: 'Update menu, prices & availability instantly via our menu management tools.' },
            { icon: '🪑', name: 'Table Management', desc: 'Optimize floor plan and track all orders by table.' }
        ]
    },
    {
        title: '🎁 Hardware & Support',
        features: [
            { icon: '🖨️', name: 'Bill Printer Hardware', desc: `Mini thermal printer available as a one-time purchase (₹${BILL_PRINTER_PRICE}).` },
            { icon: '🎓', name: 'Free Training', desc: 'Complete staff training included.' },
            { icon: '⚡', name: '24-Hour Setup', desc: 'Get started within 24 hours.' },
            { icon: '🛡️', name: '24/7 Support', desc: 'Round-the-clock technical support.' }
        ]
    }
];

// --- HOTEL FEATURES (For Features Page - NEW) ---
const HOTEL_FEATURES = [
    {
        title: '🏨 In-Room QR Ordering System (Replace Telephonic Orders)',
        features: [
            { icon: '🔑', name: 'Unique QR Code Per Room', desc: 'Customers scan a unique QR in their room to order instantly, eliminating call center dependency.' },
            { icon: '🔔', name: 'Real-time Staff Notifications', desc: 'Instant WhatsApp-like alerts to the counter/manager app with room number and amount.' },
            { icon: '⏱️', name: 'Customer Prep Time Visibility', desc: 'Staff updates preparation time; the customer sees the live countdown on their device.' },
            { icon: '✅', name: 'Order Status Updates', desc: 'One-click marks for "Ready" and "Delivered" visible to the customer.' },
        ]
    },
    {
        title: '📊 Advanced Hotel Analytics & Feedback',
        features: [
            { icon: '📈', name: 'Comprehensive Sales Analytics', desc: 'Track total sales per day, week, and month.' },
            { icon: '🏆', name: 'Peak Hours & High Sellers', desc: 'Analyze peak ordering times and top-selling food items for inventory optimization.' },
            { icon: '⭐', name: 'One-Click Google Review Popup', desc: 'After delivery, a Google Review link pops up on the customer’s device for instant feedback.' },
            { icon: '✍️', name: 'Create Manual Bill', desc: 'Ability for staff to generate bills for offline or non-QR orders.' }
        ]
    },
    {
        title: '⚙️ Operations & Management',
        features: [
            { icon: '🍽️', name: 'Digital Menu Management', desc: 'Update in-room dining menus, prices & availability instantly.' },
            { icon: '👨‍💼', name: 'Employee Management', desc: 'Track staff performance and order handling time.' },
            { icon: '🔒', name: 'Fraud Monitoring', desc: 'Monitor staff billing activity for discrepancies.' },
            { icon: '🌐', name: 'Web/App Login for Staff', desc: 'Access notifications and management dashboard on multiple devices.' }
        ]
    },
    {
        title: '🎁 Hardware & Support',
        features: [
            { icon: '🖨️', name: 'Bill Printer Hardware', desc: `Mini thermal printer available as a one-time purchase (₹${BILL_PRINTER_PRICE}).` },
            { icon: '🎓', name: 'Free Training', desc: 'Complete staff training included.' },
            { icon: '⚡', name: '24-Hour Setup', desc: 'Get started within 24 hours.' },
            { icon: '🛡️', name: '24/7 Support', desc: 'Round-the-clock technical support.' }
        ]
    }
];


const AdminLoginForm = ({ setAdminLoggedIn, setAdminUser }) => {
    const [showPassword, setShowPassword] = useState(false);
    const [adminEmail, setAdminEmail] = useState('');
    const [adminPassword, setAdminPassword] = useState('');
    const [loginError, setLoginError] = useState('');
    const [isLoggingIn, setIsLoggingIn] = useState(false);

    const handleAdminLogin = async (e) => {
        e.preventDefault();
        setLoginError('');
        setIsLoggingIn(true);
        
        try {
            const userCredential = await signInWithEmailAndPassword(auth, adminEmail, adminPassword);
            setAdminLoggedIn(true);
            setAdminUser(userCredential.user);
            setAdminEmail('');
            setAdminPassword('');
        } catch (error) {
            let errorMessage = 'Login failed. Please check your credentials.';
            
            if (error.code === 'auth/invalid-credential' || error.code === 'auth/wrong-password') {
                errorMessage = 'Invalid email or password.';
            } else if (error.code === 'auth/user-not-found') {
                errorMessage = 'No admin account found with this email.';
            } else if (error.code === 'auth/too-many-requests') {
                errorMessage = 'Too many failed attempts. Please try again later.';
            }
            
            setLoginError(errorMessage);
        } finally {
            setIsLoggingIn(false);
        }
    };

    return (
        <div className="bg-white/95 backdrop-blur-xl p-10 rounded-3xl shadow-2xl max-w-md w-full border border-orange-100 transform hover:scale-105 transition-all duration-500">
            <div className="text-center mb-8">
                <div className="inline-block p-4 bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl mb-4 shadow-lg">
                    <Lock className="text-white" size={40} />
                </div>
                <h2 className="text-4xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">Admin Portal</h2>
                <p className="text-gray-600 mt-2">Access your powerful dashboard</p>
            </div>
            <form onSubmit={handleAdminLogin} className="space-y-5">
                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address</label>
                    <input
                        key="admin-email-input"
                        type="email"
                        value={adminEmail}
                        onChange={(e) => setAdminEmail(e.target.value)}
                        className="w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all bg-gray-50 hover:bg-white"
                        placeholder="admin@zyppro.com"
                        required
                    />
                </div>
                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Password</label>
                    <div className="relative">
                        <input
                            key="admin-password-input"
                            type={showPassword ? "text" : "password"}
                            value={adminPassword}
                            onChange={(e) => setAdminPassword(e.target.value)}
                            className="w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all bg-gray-50 hover:bg-white"
                            placeholder="Enter your password"
                            required
                        />
                        <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-orange-600 transition-colors"
                        >
                            {showPassword ? <EyeOff size={22} /> : <Eye size={22} />}
                        </button>
                    </div>
                </div>
                
                {loginError && (
                    <div className="bg-red-50 border-l-4 border-red-500 text-red-700 px-5 py-4 rounded-lg text-sm animate-pulse">
                        <div className="flex items-center gap-2">
                            <AlertCircle size={18} />
                            {loginError}
                        </div>
                    </div>
                )}
                
                <button
                    type="submit"
                    disabled={isLoggingIn}
                    className="w-full bg-gradient-to-r from-orange-500 via-red-500 to-red-600 text-white py-4 rounded-xl font-bold shadow-xl hover:shadow-2xl transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex justify-center items-center gap-2 relative overflow-hidden group"
                >
                    <div className="absolute inset-0 bg-gradient-to-r from-orange-400 to-red-500 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                    <span className="relative z-10">
                        {isLoggingIn ? (
                            <>
                                <Clock size={20} className="animate-spin inline mr-2" /> Authenticating...
                            </>
                        ) : (
                            <>
                                <Shield size={20} className="inline mr-2" /> Access Dashboard
                            </>
                        )}
                    </span>
                </button>
                <p className="text-xs text-gray-500 text-center mt-4 flex items-center justify-center gap-2">
                    <Lock size={14} /> Secured by Zyppro
                </p>
            </form>
        </div>
    );
};

//new
// --- NEW CHATBOT COMPONENT (Paste this before const ZypproWebsite) ---
// --- NEW CHATBOT COMPONENT WITH QUICK OPTIONS ---
// --- UPDATED CHATBOT WITH AUTO-SCROLL ---
// --- FIXED CHATBOT WITH SMART SCROLLING ---
const ChatBot = () => {
    const [isOpen, setIsOpen] = useState(false);
    
    // Reference to the latest message to control scrolling
    const lastMessageRef = useRef(null);

    const [messages, setMessages] = useState([
        { 
            type: 'bot', 
            text: 'Hi! 👋 Welcome to Zyppro. How can I help you today?', 
            showOptions: true 
        }
    ]);
    const [inputText, setInputText] = useState('');
    const [isTyping, setIsTyping] = useState(false);

    // --- SMART SCROLL EFFECT ---
    useEffect(() => {
        if (isOpen && lastMessageRef.current) {
            // This scrolls so the TOP of the new message is visible, 
            // ensuring the user reads the answer first.
            lastMessageRef.current.scrollIntoView({ 
                behavior: "smooth", 
                block: "start" 
            });
        }
    }, [messages, isTyping, isOpen]);

    // Knowledge Base
    const getBotResponse = (input) => {
        const lowerInput = input.toLowerCase();

        if (lowerInput.includes('price') || lowerInput.includes('cost') || lowerInput.includes('plan')) return "We have plans starting at ₹499/month for restaurants and ₹299/month for hotels. You can view our Pricing section for details!";
        if (lowerInput.includes('demo')) return "You can book a free demo by clicking the 'Book Free Demo' button at the top of the page.";
        
        // Updated Answers with Push Notification info
        if (lowerInput.includes('hotel')) return "Our Hotel package replaces telephonic ordering with QR codes. Staff receive instant WhatsApp-like push notifications showing exactly which room ordered, ensuring no order is ever missed.";
        if (lowerInput.includes('restaurant') || lowerInput.includes('pos')) return "Our Restaurant POS handles QR ordering, billing, and inventory. Staff get real-time WhatsApp-like alerts with table numbers to process orders immediately without missing anything.";
        
        if (lowerInput.includes('features')) return "We offer QR Ordering, Auto Billing, Inventory Management, Staff Tracking, and detailed Analytics.";
        if (lowerInput.includes('printer') || lowerInput.includes('hardware')) return "We offer a Mini Thermal Bill Printer for a one-time cost of ₹2000.";
        if (lowerInput.includes('hello') || lowerInput.includes('hi')) return "Hello! Please select an option below.";
        
        return "fallback"; 
    };

    const handleSend = (e) => {
        e.preventDefault();
        if (!inputText.trim()) return;
        processUserMessage(inputText);
    };

    const processUserMessage = (text) => {
        const userMsg = { type: 'user', text: text };
        setMessages(prev => [...prev, userMsg]);
        setInputText('');
        setIsTyping(true);

        setTimeout(() => {
            const responseText = getBotResponse(text);
            setIsTyping(false);

            let botMsg = { type: 'bot', text: responseText };

            // Always show options to keep engagement
            botMsg.showOptions = true; 
            
            if (responseText === 'fallback') {
                botMsg.text = "I'm not sure about that specific detail. You can select an option below or contact us directly:";
            }

            setMessages(prev => [...prev, botMsg]);
        }, 800);
    };

    const handleOptionClick = (action) => {
        if (action === 'whatsapp') {
            window.open('https://wa.me/918178903697?text=Hi,%20I%20have%20a%20query%20regarding%20Zyppro', '_blank');
        } else if (action === 'call') {
            window.location.href = 'tel:+918178903697';
        } else {
            processUserMessage(action);
        }
    };

    // The Options Component
    const QuickOptions = () => (
        <div className="mt-3 flex flex-col space-y-2 bg-white rounded-xl p-1 border border-gray-100">
            <button 
                onClick={() => handleOptionClick('What is the price?')}
                className="flex items-center gap-2 w-full text-left p-2 hover:bg-blue-50 rounded-lg transition-colors group"
            >
                <ChevronRight size={16} className="text-blue-600 flex-shrink-0" />
                <span className="text-blue-600 font-bold text-sm group-hover:underline">Ask for Price</span>
            </button>

            <button 
                onClick={() => handleOptionClick('What are the restaurant features?')}
                className="flex items-center gap-2 w-full text-left p-2 hover:bg-blue-50 rounded-lg transition-colors group"
            >
                <ChevronRight size={16} className="text-blue-600 flex-shrink-0" />
                <span className="text-blue-600 font-bold text-sm group-hover:underline">Restaurant Features</span>
            </button>

            <button 
                onClick={() => handleOptionClick('What are the hotel features?')}
                className="flex items-center gap-2 w-full text-left p-2 hover:bg-blue-50 rounded-lg transition-colors group"
            >
                <ChevronRight size={16} className="text-blue-600 flex-shrink-0" />
                <span className="text-blue-600 font-bold text-sm group-hover:underline">Hotel Features</span>
            </button>

            <button 
                onClick={() => handleOptionClick('Tell me about the printer')}
                className="flex items-center gap-2 w-full text-left p-2 hover:bg-blue-50 rounded-lg transition-colors group"
            >
                <ChevronRight size={16} className="text-blue-600 flex-shrink-0" />
                <span className="text-blue-600 font-bold text-sm group-hover:underline">Printer Hardware</span>
            </button>

            <button 
                onClick={() => handleOptionClick('whatsapp')}
                className="flex items-center gap-2 w-full text-left p-2 hover:bg-blue-50 rounded-lg transition-colors group"
            >
                <ChevronRight size={16} className="text-blue-600 flex-shrink-0" />
                <span className="text-blue-600 font-bold text-sm group-hover:underline">Chat on WhatsApp</span>
            </button>

            <button 
                onClick={() => handleOptionClick('call')}
                className="flex items-center gap-2 w-full text-left p-2 hover:bg-blue-50 rounded-lg transition-colors group"
            >
                <ChevronRight size={16} className="text-blue-600 flex-shrink-0" />
                <span className="text-blue-600 font-bold text-sm group-hover:underline">Call Us Directly</span>
            </button>
        </div>
    );

    return (
        <div className="fixed bottom-6 right-6 z-50 font-sans">
            {isOpen && (
                <div className="bg-white rounded-2xl shadow-2xl w-80 sm:w-96 mb-4 border border-orange-100 overflow-hidden flex flex-col transition-all duration-300 transform origin-bottom-right">
                    <div className="bg-gradient-to-r from-orange-500 to-red-600 p-4 flex justify-between items-center text-white">
                        <div className="flex items-center gap-2">
                            <div className="bg-white/20 p-2 rounded-full">
                                <MessageCircle size={20} />
                            </div>
                            <div>
                                <h3 className="font-bold">Zyppro Assistant</h3>
                                <p className="text-xs text-orange-100">● Online</p>
                            </div>
                        </div>
                        <button onClick={() => setIsOpen(false)} className="hover:bg-white/20 p-1 rounded-full transition-colors">
                            <X size={20} />
                        </button>
                    </div>

                    <div className="h-96 overflow-y-auto p-4 bg-gray-50 space-y-4 scroll-smooth">
                        {messages.map((msg, idx) => {
                            // Check if this is the last message to attach the ref
                            const isLastMessage = idx === messages.length - 1;
                            
                            return (
                                <div 
                                    key={idx} 
                                    ref={isLastMessage ? lastMessageRef : null} // Attach ref here
                                    className={`flex flex-col ${msg.type === 'user' ? 'items-end' : 'items-start'}`}
                                >
                                    <div className={`max-w-[85%] p-3 rounded-2xl text-sm shadow-sm ${
                                        msg.type === 'user' 
                                            ? 'bg-orange-500 text-white rounded-tr-none' 
                                            : 'bg-white text-gray-700 border border-gray-100 rounded-tl-none'
                                    }`}>
                                        {msg.text}
                                    </div>
                                    {msg.showOptions && msg.type === 'bot' && (
                                        <div className="w-[85%] mt-1">
                                            <QuickOptions />
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                        
                        {isTyping && (
                            <div className="flex justify-start">
                                <div className="bg-white p-3 rounded-2xl rounded-tl-none border border-gray-100 shadow-sm flex gap-1">
                                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></span>
                                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-75"></span>
                                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-150"></span>
                                </div>
                            </div>
                        )}
                    </div>

                    <form onSubmit={handleSend} className="p-3 bg-white border-t border-gray-100 flex gap-2">
                        <input
                            type="text"
                            value={inputText}
                            onChange={(e) => setInputText(e.target.value)}
                            placeholder="Type a message..."
                            className="flex-1 bg-gray-100 rounded-xl px-4 py-2 text-sm focus:ring-2 focus:ring-orange-500 outline-none"
                        />
                        <button 
                            type="submit"
                            className="bg-orange-600 text-white p-2 rounded-xl hover:bg-orange-700 transition-colors"
                        >
                            <Send size={20} />
                        </button>
                    </form>
                </div>
            )}

            <button
                onClick={() => setIsOpen(!isOpen)}
                className="bg-gradient-to-r from-orange-500 to-red-600 w-14 h-14 rounded-full shadow-2xl flex items-center justify-center text-white hover:scale-110 transition-transform duration-300 relative group"
            >
                {isOpen ? <X size={28} /> : <MessageCircle size={28} />}
                {!isOpen && (
                    <span className="absolute -top-1 -right-1 flex h-4 w-4">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-4 w-4 bg-red-500"></span>
                    </span>
                )}
            </button>
        </div>
    );
};


//new
const ZypproWebsite = () => {
    const [currentPage, setCurrentPage] = useState('home');
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const [adminLoggedIn, setAdminLoggedIn] = useState(false);
    const [adminUser, setAdminUser] = useState(null);
    // --- State used by both Home/Features/Pricing for type toggle ---
    const [businessType, setBusinessType] = useState('restaurant'); // 'restaurant' or 'hotel'
    
    const [demoRequests, setDemoRequests] = useState([]);
    const [packages, setPackages] = useState(DEFAULT_PACKAGES);
    const [editingPackage, setEditingPackage] = useState(null);
    const [showAddPackage, setShowAddPackage] = useState(false);
    const [newPackage, setNewPackage] = useState({
        name: '',
        price: '',
        qrTables: '',
        features: ['']
    });

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);


    useEffect(() => {
        // Scrolls the window to the top (0, 0) whenever the current page changes.
        window.scrollTo(0, 0);
    }, [currentPage]);


    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) {
                setAdminLoggedIn(true);
                setAdminUser(user);
            } else {
                setAdminLoggedIn(false);
                setAdminUser(null);
            }
        });
        return () => unsubscribe();
    }, []);

    // --- ADDED useEffect TO RELOAD PACKAGES WHEN TYPE CHANGES ---
    useEffect(() => {
        loadPackages();
    }, [businessType]);
    // -----------------------------------------------------------


    useEffect(() => {
        if (adminLoggedIn && currentPage === 'admin-login') {
            loadDemoRequests();
        }
    }, [adminLoggedIn, currentPage]);

    const getPackagesForType = (type) => {
        return type === 'hotel' ? HOTEL_PACKAGES : DEFAULT_PACKAGES;
    }

    const initializePackages = async () => {
        try {
            // Only initialize Restaurant packages in Firestore
            const docRef = doc(db, 'config', 'packages');
            await setDoc(docRef, { packages: DEFAULT_PACKAGES });
            setPackages(DEFAULT_PACKAGES);
        } catch (error) {
            console.error('Error initializing packages:', error);
        }
    };

    // --- MODIFIED loadPackages TO USE businessType ---
    const loadPackages = async () => {
        try {
            if (businessType === 'hotel') {
                // Hotel packages are currently static/local, load them directly
                setPackages(HOTEL_PACKAGES);
                return;
            }
            
            // Load Restaurant packages from Firestore
            const docRef = doc(db, 'config', 'packages');
            const docSnap = await getDoc(docRef);
            
            if (docSnap.exists()) {
                const data = docSnap.data();
                if (data.packages && Array.isArray(data.packages) && data.packages.length > 0) {
                    setPackages(data.packages);
                } else {
                    await initializePackages();
                }
            } else {
                await initializePackages();
            }
        } catch (error) {
            console.error('Error loading packages:', error);
            // Fallback to local default packages if Firestore fails
            setPackages(getPackagesForType(businessType)); 
        }
    };
    // ----------------------------------------------------

    const loadDemoRequests = async () => {
        try {
            const querySnapshot = await getDocs(collection(db, 'demo-requests'));
            const requests = [];
            
            querySnapshot.forEach((docSnapshot) => {
                requests.push({
                    id: docSnapshot.id,
                    ...docSnapshot.data()
                });
            });
            
            setDemoRequests(requests.sort((a, b) =>
                new Date(b.timestamp) - new Date(a.timestamp)
            ));
        } catch (error) {
            console.error('Error loading demo requests:', error);
        }
    };

    const submitDemoRequest = async (formData) => {
        try {
            const requestData = {
                ...formData,
                timestamp: new Date().toISOString(),
                status: 'pending'
            };
            
            await addDoc(collection(db, 'demo-requests'), requestData);
            alert('🎉 Thank you! We will contact you soon for a demo.');
            return true;
        } catch (error) {
            console.error('Error submitting demo request:', error);
            alert('❌ Error submitting request. Please try again.');
            return false;
        }
    };

    const updateRequestStatus = async (requestId, newStatus) => {
        try {
            const docRef = doc(db, 'demo-requests', requestId);
            await updateDoc(docRef, { status: newStatus });
            await loadDemoRequests();
        } catch (error) {
            console.error('Error updating status:', error);
        }
    };

    const deleteRequest = async (requestId) => {
        if (window.confirm('Are you sure you want to delete this request?')) {
            try {
                await deleteDoc(doc(db, 'demo-requests', requestId));
                await loadDemoRequests();
                alert('✅ Request deleted!');
            } catch (error) {
                console.error('Error deleting request:', error);
            }
        }
    };

    const updatePackage = async (packageId, updatedPackage) => {
        try {
            const newPackages = packages.map(pkg =>
                pkg.id === packageId ? updatedPackage : pkg
            );
            setPackages(newPackages);
            
            // Only update Firestore for Restaurant packages (assuming Admin only manages Restaurant packages)
            if (businessType === 'restaurant') {
                await setDoc(doc(db, 'config', 'packages'), { packages: newPackages.filter(p => p.type === 'restaurant') });
            }
            alert('✅ Package updated successfully!');
            setEditingPackage(null);
        } catch (error) {
            console.error('Error updating package:', error);
        }
    };

    const deletePackage = async (packageId) => {
        if (window.confirm('Are you sure you want to delete this package?')) {
            try {
                const newPackages = packages.filter(pkg => pkg.id !== packageId);
                setPackages(newPackages);
                
                // Only update Firestore for Restaurant packages
                if (businessType === 'restaurant') {
                    await setDoc(doc(db, 'config', 'packages'), { packages: newPackages.filter(p => p.type === 'restaurant') });
                }
                alert('✅ Package deleted!');
            } catch (error) {
                console.error('Error deleting package:', error);
            }
        }
    };

    const addNewPackage = async () => {
        if (!newPackage.name || !newPackage.price || (!newPackage.qrTables && businessType === 'restaurant')) {
            alert('Please fill all required fields');
            return;
        }

        try {
            const packageToAdd = {
                id: Date.now(),
                name: newPackage.name,
                price: parseInt(newPackage.price),
                qrTables: parseInt(newPackage.qrTables || 0), // Use 0 or appropriate default for hotel if not applicable
                features: newPackage.features.filter(f => f.trim() !== ''),
                popular: false,
                type: businessType // Store type
            };

            const newPackages = [...packages, packageToAdd];
            setPackages(newPackages);
            
            // Only update Firestore for Restaurant packages
            if (businessType === 'restaurant') {
                await setDoc(doc(db, 'config', 'packages'), { packages: newPackages.filter(p => p.type === 'restaurant') });
            }
            
            alert('✅ Package added successfully!');
            
            setShowAddPackage(false);
            setNewPackage({ name: '', price: '', qrTables: '', features: [''] });
        } catch (error) {
            console.error('Error adding package:', error);
        }
    };

    const handleAdminLogout = async () => {
        try {
            await signOut(auth);
            setAdminLoggedIn(false);
            setAdminUser(null);
            setCurrentPage('home');
        } catch (error) {
            console.error('Logout error:', error);
        }
    };

    const Navigation = () => (
        <nav className={`fixed w-full z-50 transition-all duration-500 ${isScrolled ? 'bg-white/98 backdrop-blur-2xl shadow-2xl border-b border-gray-100' : 'bg-transparent'}`}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center h-24">
                    <div className="flex items-center cursor-pointer group" onClick={() => setCurrentPage('home')}>
                         <div className="text-3xl font-bold relative">
                            <img
                                src="/logo512.png"
                                alt="Zyppro Restaurant POS System Logo"
                                className={`h-16 transition-all duration-500 transform group-hover:scale-110 ${isScrolled ? 'brightness-100' : 'brightness-200 drop-shadow-2xl'}`}
                            />
                            <div className="absolute inset-0 bg-gradient-to-r from-orange-500 to-red-600 opacity-0 group-hover:opacity-20 blur-xl transition-opacity"></div>
                        </div>
                    </div>
                    
                    <div className="hidden md:flex space-x-1 bg-white/10 backdrop-blur-lg rounded-full p-2">
                        {['home', 'features', 'pricing', 'contact'].map(page => (
                            <button
                                key={page}
                                onClick={() => setCurrentPage(page)}
                                className={`capitalize font-semibold px-6 py-3 rounded-full transition-all duration-300 relative ${
                                    currentPage === page
                                        ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white shadow-lg transform scale-105'
                                        : isScrolled
                                            ? 'text-gray-700 hover:bg-orange-50 hover:text-orange-600'
                                            : 'text-white hover:bg-white/20'
                                }`}
                            >
                                {page}
                            </button>
                        ))}
                        {adminLoggedIn && (
                            <button
                                onClick={() => setCurrentPage('admin-login')}
                                className={`capitalize font-semibold px-6 py-3 rounded-full transition-all duration-300 relative ${
                                    currentPage === 'admin-login'
                                        ? 'bg-gradient-to-r from-red-500 to-pink-600 text-white shadow-lg transform scale-105'
                                        : isScrolled
                                            ? 'text-gray-700 hover:bg-red-50 hover:text-red-600'
                                            : 'text-white hover:bg-white/20'
                                }`}
                            >
                                Admin
                            </button>
                        )}
                    </div>

                    <button
                        className="md:hidden transition-all duration-300 hover:scale-110 p-2 rounded-xl hover:bg-orange-100"
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                    >
                        {mobileMenuOpen ? (
                            <X className={`${isScrolled ? 'text-gray-700' : 'text-white'}`} size={28} />
                        ) : (
                            <Menu className={`${isScrolled ? 'text-gray-700' : 'text-white'}`} size={28} />
                        )}
                    </button>
                </div>
            </div>

            {mobileMenuOpen && (
                <div className="md:hidden bg-white/98 backdrop-blur-2xl shadow-2xl rounded-b-3xl mx-4">
                    {['home', 'features', 'pricing', 'contact'].map(page => (
                        <button
                            key={page}
                            onClick={() => {
                                setCurrentPage(page);
                                setMobileMenuOpen(false);
                            }}
                            className="block w-full text-left px-8 py-5 capitalize hover:bg-gradient-to-r hover:from-orange-50 hover:to-red-50 transition-all duration-300 font-semibold text-gray-700 hover:text-orange-600 border-b border-gray-100 last:border-0"
                        >
                            {page}
                        </button>
                    ))}
                    {adminLoggedIn && (
                        <button
                            onClick={() => {
                                setCurrentPage('admin-login');
                                setMobileMenuOpen(false);
                            }}
                            className="block w-full text-left px-8 py-5 capitalize hover:bg-gradient-to-r hover:from-red-50 hover:to-pink-50 transition-all duration-300 font-semibold text-gray-700 hover:text-red-600 border-b border-gray-100 last:border-0"
                        >
                            Admin
                        </button>
                    )}
                </div>
            )}
        </nav>
    );


    // --- VIDEO SECTION COMPONENT (START) ---
    const VideoWalkthroughSection = () => (
        <div className="py-32 bg-gray-900 relative overflow-hidden">
            <div className="absolute inset-0 opacity-10" style={{
                backgroundImage: `radial-gradient(circle at 50% 50%, #f97316 1px, transparent 1px)`,
                backgroundSize: '50px 50px'
            }}></div>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
                <div className="inline-block px-6 py-3 bg-gradient-to-r from-yellow-400 to-orange-500 text-gray-900 rounded-full text-sm font-black mb-6 shadow-lg">
                    <Eye className="inline mr-2" size={16} /> LIVE DEMO
                </div>
                <h2 className="text-5xl md:text-7xl font-black text-white mb-12">
                    See Zyppro RMS in <span className="text-orange-400">Action</span>
                </h2>

                {/* The YouTube Video Embed */}
                <div className="aspect-video w-full max-w-5xl mx-auto shadow-[0_0_80px_rgba(251,146,60,0.5)] rounded-3xl overflow-hidden border-8 border-orange-400 transition-all duration-500 hover:shadow-[0_0_100px_rgba(251,146,60,0.8)]">
                    <iframe
                        width="100%"
                        height="100%"
                        src="https://www.youtube.com/embed/GJ06hWE4NUs?rel=0&autoplay=0&modestbranding=1"
                        title="Zyppro RMS Dashboard Walkthrough"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                        className="w-full h-full"
                    ></iframe>
                </div>

                <div className="mt-16">
                    <p className="text-xl text-gray-400 mb-6 max-w-3xl mx-auto">
                        The video above showcases the full potential of our system, from QR Ordering to advanced Analytics.
                    </p>
                    <button
                        onClick={() => setCurrentPage('contact')}
                        className="bg-gradient-to-r from-orange-500 to-red-600 text-white px-10 py-5 rounded-full text-xl font-black hover:shadow-2xl hover:shadow-red-500/50 transition-all transform hover:scale-105 inline-flex items-center gap-3 group relative overflow-hidden"
                    >
                        <Target className="group-hover:rotate-90 transition-transform duration-500" size={28} />
                        Book Free Demo Now
                    </button>
                </div>
            </div>
        </div>
    );
    // --- VIDEO SECTION COMPONENT (END) ---


    const HomePage = () => {
        
        // --- NEW LINE: Define packages based on current type ---
        const currentPackages = businessType === 'hotel' ? HOTEL_PACKAGES : packages;
        // -------------------------------------------------------

        return (
            <div className="bg-white">
                <style>{`
                    @keyframes float {
                        0%, 100% { transform: translateY(0px) rotate(0deg); }
                        50% { transform: translateY(-20px) rotate(5deg); }
                    }
                    @keyframes pulse-glow {
                        0%, 100% { box-shadow: 0 0 40px rgba(251, 146, 60, 0.4); }
                        50% { box-shadow: 0 0 80px rgba(251, 146, 60, 0.6); }
                    }
                    .float-animation {
                        animation: float 6s ease-in-out infinite;
                    }
                    .gradient-text {
                        background: linear-gradient(135deg, #f97316 0%, #dc2626 100%);
                        -webkit-background-clip: text;
                        -webkit-text-fill-color: transparent;
                        background-clip: text;
                    }
                `}</style>

                {/* Hero Section - Enhanced */}
                <div className="relative min-h-screen bg-gradient-to-br from-orange-500 via-orange-600 to-red-600 overflow-hidden">
                    {/* Animated Background */}
                    <div className="absolute inset-0">
                        <div className="absolute inset-0 opacity-20" style={{
                            backgroundImage: `radial-gradient(circle at 20% 50%, rgba(255,255,255,0.2) 0%, transparent 50%),
                                             radial-gradient(circle at 80% 80%, rgba(255,255,255,0.2) 0%, transparent 50%),
                                             radial-gradient(circle at 40% 20%, rgba(255,255,255,0.1) 0%, transparent 50%)`
                        }}></div>
                        <div className="absolute inset-0 opacity-10" style={{
                            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%E3%83%BC%3C/svg%3E")`,
                            backgroundSize: '60px 60px'
                        }}></div>
                    </div>
                    
                    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-40 pb-24">
                        <div className="text-center">
                            <div className="inline-block mb-6 float-animation">
                                <div className="text-9xl drop-shadow-2xl">🍽️</div>
                            </div>
                            <h1 className="text-6xl md:text-8xl font-black text-white mb-8 leading-tight tracking-tight">
                                Restaurant POS System<br />
                                <span className="text-yellow-300 drop-shadow-lg">Simplified</span>
                            </h1>
                            <p className="text-2xl md:text-3xl text-white/95 mb-10 max-w-4xl mx-auto font-light leading-relaxed">
                                Complete Restaurant Management with <span className="font-bold">QR Ordering</span>, <span className="font-bold">Billing Software</span>, <span className="font-bold">Analytics</span> & More
                            </p>
                            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
                                <button
                                    onClick={() => setCurrentPage('contact')}
                                    className="bg-white text-orange-600 px-10 py-5 rounded-full text-xl font-bold hover:bg-yellow-300 transition-all transform hover:scale-110 shadow-2xl flex items-center gap-3 group relative overflow-hidden"
                                    style={{animation: 'pulse-glow 2s ease-in-out infinite'}}
                                >
                                    <span className="relative z-10">Book Free Demo</span>
                                    <ChevronRight className="relative z-10 group-hover:translate-x-2 transition-transform" />
                                    <div className="absolute inset-0 bg-gradient-to-r from-orange-400 to-red-500 opacity-0 group-hover:opacity-20 transition-opacity"></div>
                                </button>
                                <button
                                    onClick={() => setCurrentPage('pricing')}
                                    className="bg-white/10 backdrop-blur-lg border-2 border-white text-white px-10 py-5 rounded-full text-xl font-bold hover:bg-white hover:text-orange-600 transition-all transform hover:scale-110 shadow-xl"
                                >
                                    View Pricing
                                </button>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-24">
                            {[
                                { icon: '⚡', value: '2X', label: 'Faster Service', color: 'from-yellow-400 to-orange-500' },
                                { icon: '💰', value: '30%', label: 'Revenue Increase', color: 'from-green-400 to-emerald-600' },
                                { icon: '✅', value: '0', label: 'Order Errors', color: 'from-blue-400 to-indigo-600' },
                                { icon: '📊', value: '100%', label: 'Data Visibility', color: 'from-purple-400 to-pink-600' }
                            ].map((stat, idx) => (
                                <div key={idx} className="bg-white/15 backdrop-blur-xl rounded-3xl p-8 text-center border border-white/30 hover:bg-white/25 transition-all duration-500 hover:scale-110 hover:rotate-2 shadow-2xl group">
                                    <div className="text-5xl mb-4 transform group-hover:scale-125 transition-transform">{stat.icon}</div>
                                    <div className={`text-5xl font-black text-white mb-2 bg-gradient-to-r ${stat.color} bg-clip-text text-transparent`}>{stat.value}</div>
                                    <div className="text-white/90 text-sm font-semibold">{stat.label}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Features Section - Enhanced */}
                <div className="py-32 bg-gradient-to-b from-white via-orange-50/30 to-white relative overflow-hidden">
                    <div className="absolute inset-0 opacity-5" style={{
                        backgroundImage: `radial-gradient(circle at 50% 50%, #f97316 1px, transparent 1px)`,
                        backgroundSize: '50px 50px'
                    }}></div>
                    
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                        <div className="text-center mb-20">
                            <div className="inline-block px-6 py-3 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-full text-sm font-bold mb-6 shadow-lg">
                                <Sparkles className="inline mr-2" size={16} /> POWERFUL FEATURES
                            </div>
                            <h2 className="text-5xl md:text-7xl font-black text-gray-900 mb-6">
                                <span className="gradient-text">Complete</span> Feature Set
                            </h2>
                            <p className="text-2xl text-gray-600 max-w-3xl mx-auto">Everything you need to run a successful restaurant, all in one powerful POS system</p>
                        </div>

                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                            {[
                                { icon: '📱', title: 'QR Menu & Ordering', desc: 'Customers scan & order directly from their phones using our QR scanner.', gradient: 'from-blue-500 to-cyan-600' },
                                { icon: '🧾', title: 'Auto Billing & GST', desc: 'Automatic bill generation with full GST compliance using our billing software.', gradient: 'from-green-500 to-emerald-600' },
                                { icon: '📊', title: 'Analytics Dashboard', desc: 'Real-time sales insights for better decision making.', gradient: 'from-purple-500 to-pink-600' },
                                { icon: '🍔', title: 'Menu Management', desc: 'Update menu items, photos, and prices instantly via our menu management tools.', gradient: 'from-orange-500 to-red-600' },
                                { icon: '📦', title: 'Inventory Tracking', desc: 'Real-time stock tracking & low-stock alerts.', gradient: 'from-yellow-500 to-orange-600' },
                                { icon: '💰', title: 'Profit/Loss Dashboard', desc: 'Clear financial view of daily, weekly, monthly profits.', gradient: 'from-indigo-500 to-purple-600' },
                                { icon: '🔒', title: 'Fraud Detection', desc: 'Monitor cashier billing activity for suspicious patterns.', gradient: 'from-red-500 to-pink-600' },
                                { icon: '👥', title: 'Employee Management', desc: 'Track staff performance, shifts & attendance.', gradient: 'from-teal-500 to-cyan-600' },
                                { icon: '🔔', title: 'Push Notifications', desc: 'WhatsApp-like updates for order status.', gradient: 'from-green-500 to-teal-600' },
                                { icon: '⏱️', title: 'Real-time Updates', desc: 'Live order status for kitchen and customer visibility.', gradient: 'from-orange-500 to-amber-600' },
                                { icon: '🪑', title: 'Table Management', desc: 'Optimize floor plan and track all orders by table.', gradient: 'from-blue-500 to-indigo-600' },
                                { icon: '📈', title: 'Growth Analytics', desc: 'Identify business trends and key areas for growth.', gradient: 'from-pink-500 to-rose-600' }
                            ].map((feature, idx) => (
                                <div key={idx} className="group relative bg-white rounded-3xl p-8 border-2 border-gray-100 hover:border-transparent hover:shadow-2xl transition-all duration-500 hover:scale-105 overflow-hidden">
                                    <div className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-500`}></div>
                                    <div className="relative z-10">
                                        <div className="text-6xl mb-6 transform group-hover:scale-125 group-hover:rotate-12 transition-all duration-500">{feature.icon}</div>
                                        <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-orange-600 transition-colors">{feature.title}</h3>
                                        <p className="text-gray-600 leading-relaxed">{feature.desc}</p>
                                    </div>
                                    <div className={`absolute -bottom-4 -right-4 w-24 h-24 bg-gradient-to-br ${feature.gradient} rounded-full opacity-0 group-hover:opacity-20 blur-2xl transition-all duration-500`}></div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {VideoWalkthroughSection()}
                

                {/* Pricing Section - Enhanced (MODIFIED FOR TOGGLE) */}
                <div className="py-32 bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white relative overflow-hidden">
                    <div className="absolute inset-0 opacity-10" style={{
                        backgroundImage: `linear-gradient(to right, #f97316 1px, transparent 1px), linear-gradient(to bottom, #f97316 1px, transparent 1px)`,
                        backgroundSize: '100px 100px'
                    }}></div>
                    
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                        <div className="text-center mb-20">
                            <div className="inline-block px-6 py-3 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-full text-sm font-bold mb-6 shadow-lg">
                                <Award className="inline mr-2" size={16} /> FLEXIBLE PLANS
                            </div>
                            <h2 className="text-5xl md:text-7xl font-black mb-6">
                                <span className="bg-gradient-to-r from-orange-400 to-red-500 bg-clip-text text-transparent">Simple</span> Pricing
                            </h2>
                            <p className="text-2xl text-gray-400 max-w-3xl mx-auto">Choose the perfect plan for your business</p>
                        </div>

                        {/* --- NEW: Business Type Toggle for Home Page Pricing --- */}
                        <div className="flex justify-center mb-16">
                            <div className="inline-flex rounded-full bg-gray-700 p-2 shadow-lg">
                                <button
                                    onClick={() => setBusinessType('restaurant')}
                                    className={`px-8 py-3 rounded-full text-lg font-bold transition-all duration-300 flex items-center gap-2 ${
                                        businessType === 'restaurant'
                                            ? 'bg-orange-500 text-white shadow-xl'
                                            : 'text-gray-300 hover:bg-gray-800'
                                    }`}
                                >
                                    🍽️ Restaurant Pricing
                                </button>
                                <button
                                    onClick={() => setBusinessType('hotel')}
                                    className={`px-8 py-3 rounded-full text-lg font-bold transition-all duration-300 flex items-center gap-2 ${
                                        businessType === 'hotel'
                                            ? 'bg-orange-500 text-white shadow-xl'
                                            : 'text-gray-300 hover:bg-gray-800'
                                    }`}
                                >
                                    🏨 Hotel Pricing
                                </button>
                            </div>
                        </div>
                        {/* --- END NEW Toggle --- */}

                        {/* --- MODIFIED: Use currentPackages instead of 'packages' --- */}
                        <div className="grid md:grid-cols-3 gap-10">
                            {currentPackages.map((pkg, idx) => (
                                <div key={pkg.id} className={`relative rounded-3xl overflow-hidden transition-all duration-500 hover:scale-105 ${pkg.popular ? 'transform scale-105 md:scale-110' : ''}`}>
                                   {pkg.popular && (
        <div className="absolute top-1 -left-90 z-30 transform -rotate-0">
            <div className="bg-gradient-to-r from-yellow-400 via-orange-500 to-red-600 text-white px-6 py-2 rounded-full text-sm font-black shadow-2xl flex items-center gap-2 w-64 justify-center" style={{animation: 'pulse-glow 2s ease-in-out infinite'}}>
                <Star className="fill-current" size={16} /> MOST POPULAR <Star className="fill-current" size={16} />
            </div>
        </div>
    )}
                                    <div className={`bg-gradient-to-br ${pkg.popular ? 'from-orange-500 via-red-500 to-red-600' : 'from-gray-800 to-gray-900'} p-1 h-full`}>
                                        <div className="bg-gray-900 rounded-3xl p-10 h-full flex flex-col">
                                            <div className="text-center mb-8">
                                                <h3 className="text-4xl font-black mb-4 bg-gradient-to-r from-orange-400 to-red-500 bg-clip-text text-transparent">{pkg.name}</h3>
                                                <div className="mb-6">
                                                    <span className="text-6xl font-black bg-gradient-to-r from-orange-400 to-red-500 bg-clip-text text-transparent">₹{pkg.price}</span>
                                                    <span className="text-gray-400 text-xl">/month</span>
                                                </div>
                                                {/* --- NEW: Conditional Limit Display --- */}
                                                <div className="text-md text-gray-400 font-semibold italic mt-2">
                                                    {pkg.type === 'hotel' ? `Up to ${pkg.rooms} Rooms` : `Up to ${pkg.qrTables} QR Tables`}
                                                </div>
                                                {/* --- END NEW --- */}
                                            </div>
                                            <ul className="space-y-4 mb-10 flex-grow">
                                                {pkg.features.map((feature, idx) => (
                                                    <li key={idx} className="flex items-start gap-3 text-gray-300">
                                                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-r from-green-400 to-emerald-600 flex items-center justify-center mt-0.5">
                                                            <Check className="text-white" size={14} />
                                                        </div>
                                                        <span className="text-sm leading-relaxed">{feature}</span>
                                                    </li>
                                                ))}
                                                <li className="flex items-start gap-3 text-orange-400 font-semibold pt-4 border-t border-gray-800">
                                                    <Plus size={20} className="flex-shrink-0 mt-0.5" />
                                                    <span className="text-sm">Mini Bill Printer: +₹{BILL_PRINTER_PRICE} One-Time</span>
                                                </li>
                                            </ul>
                                            <button
                                                onClick={() => setCurrentPage('contact')}
                                                className={`w-full py-5 rounded-2xl font-bold transition-all transform hover:scale-105 shadow-xl text-lg ${
                                                    pkg.popular
                                                        ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white hover:shadow-orange-500/50'
                                                        : 'bg-gray-800 text-white hover:bg-gray-700 border-2 border-gray-700'
                                                }`}
                                            >
                                                Get Started <ArrowRight className="inline ml-2" size={20} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        {/* --- END MODIFIED PACKAGE GRID --- */}

                        <div className="mt-20 bg-gradient-to-r from-yellow-500 via-orange-500 to-red-600 p-1 rounded-3xl">
                            <div className="bg-gray-900 rounded-3xl p-12 text-center">
                                <div className="text-6xl mb-6">🎉</div>
                                <h3 className="text-4xl font-black mb-4 bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">Special Launch Offer!</h3>
                                <p className="text-xl text-gray-300 mb-4">Get <span className="font-bold text-yellow-400">1 Month FREE</span> on any annual plan + Free setup & training!</p>
                                <p className="text-sm text-gray-500">* All prices exclusive of GST. No hidden charges.</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* CTA Section - Enhanced */}
                <div className="py-32 bg-gradient-to-br from-orange-500 via-red-600 to-red-700 text-white relative overflow-hidden">
                    <div className="absolute inset-0 opacity-20" style={{
                        backgroundImage: `radial-gradient(circle at 30% 30%, rgba(255,255,255,0.3) 0%, transparent 50%),
                                         radial-gradient(circle at 70% 70%, rgba(255,255,255,0.2) 0%, transparent 50%)`
                    }}></div>
                    
                    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
                        <div className="inline-block mb-8">
                            <Rocket className="text-yellow-300" size={80} />
                        </div>
                        <h2 className="text-5xl md:text-7xl font-black mb-8 leading-tight">
                            Ready to Upgrade Your<br />
                            <span className="text-yellow-300">Restaurant Billing & POS?</span>
                        </h2>
                        <p className="text-2xl mb-12 opacity-95 max-w-3xl mx-auto font-light">Start your journey to better management today with our industry-leading solution.</p>
                        <button
                            onClick={() => setCurrentPage('contact')}
                            className="bg-white text-orange-600 px-16 py-6 rounded-full text-2xl font-black hover:bg-yellow-300 transition-all transform hover:scale-110 inline-flex items-center gap-4 shadow-2xl group"
                            style={{animation: 'pulse-glow 2s ease-in-out infinite'}}
                        >
                            <Target className="group-hover:rotate-90 transition-transform duration-500" size={28} />
                            Book Free Demo
                            <ArrowRight className="group-hover:translate-x-2 transition-transform" size={28} />
                        </button>
                        <p className="mt-8 text-white/80 text-lg">⚡ Setup in 24 hours · 🎓 Free training included · 🛡️ 24/7 support</p>
                    </div>
                </div>
            </div>
        );
    };

// --- MODIFIED FeaturesPage WITH TOGGLE AND HOTEL FEATURES ---
    const FeaturesPage = () => {
        const currentFeatures = businessType === 'hotel' ? HOTEL_FEATURES : RESTAURANT_FEATURES;
        const typeTitle = businessType === 'hotel' ? 'Hotel Management' : 'Restaurant POS';
        const typeIcon = businessType === 'hotel' ? '🏨' : '🍽️';

        return (
            <div className="pt-20 bg-white min-h-screen">
                <div className="bg-gradient-to-br from-orange-500 via-red-600 to-purple-600 text-white py-28 relative overflow-hidden">
                    <div className="absolute inset-0 opacity-20" style={{
                        backgroundImage: `radial-gradient(circle at 20% 50%, rgba(255,255,255,0.3) 0%, transparent 50%),
                                         radial-gradient(circle at 80% 30%, rgba(255,255,255,0.2) 0%, transparent 50%)`
                    }}></div>
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
                        <div className="inline-block px-6 py-3 bg-white/20 backdrop-blur-lg rounded-full text-sm font-bold mb-6">
                            <Sparkles className="inline mr-2" size={16} /> COMPREHENSIVE SOLUTION
                        </div>
                        <h1 className="text-6xl md:text-7xl font-black mb-8">Complete {typeTitle}<br />Features {typeIcon}</h1>
                        <p className="text-2xl opacity-95 max-w-3xl mx-auto">Everything you need to manage your business efficiently, all in one powerful platform</p>
                    </div>
                </div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                    {/* Business Type Toggle (NEW) */}
                    <div className="flex justify-center mb-16">
                        <div className="inline-flex rounded-full bg-gray-200 p-2 shadow-lg">
                            <button
                                onClick={() => setBusinessType('restaurant')}
                                className={`px-8 py-3 rounded-full text-lg font-bold transition-all duration-300 flex items-center gap-2 ${
                                    businessType === 'restaurant'
                                        ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white shadow-xl'
                                        : 'text-gray-700 hover:bg-white'
                                }`}
                            >
                                🍽️ Restaurant Features
                            </button>
                            <button
                                onClick={() => setBusinessType('hotel')}
                                className={`px-8 py-3 rounded-full text-lg font-bold transition-all duration-300 flex items-center gap-2 ${
                                    businessType === 'hotel'
                                        ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white shadow-xl'
                                        : 'text-gray-700 hover:bg-white'
                                }`}
                            >
                                🏨 Hotel Features
                            </button>
                        </div>
                    </div>
                    {/* End Business Type Toggle */}

                    {currentFeatures.map((category, idx) => (
                        <div key={idx} className="mb-24">
                            {/* Corrected category title styling for gradient border-b */}
                            <h2 className="text-4xl font-black text-gray-900 mb-12 pb-4 border-b-4 border-transparent relative after:content-[''] after:absolute after:bottom-0 after:left-0 after:h-1 after:w-full after:bg-gradient-to-r after:from-orange-500 after:to-red-600">
                                {category.title}
                            </h2>
                            <div className="grid md:grid-cols-2 gap-8">
                                {category.features.map((feature, fIdx) => (
                                    <div key={fIdx} className="group bg-gradient-to-br from-white to-orange-50 p-8 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 hover:scale-105 border-2 border-orange-100 hover:border-orange-300">
                                        <div className="flex items-start gap-6">
                                            <div className="text-5xl transform group-hover:scale-125 group-hover:rotate-12 transition-all duration-500">{feature.icon}</div>
                                            <div>
                                                <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-orange-600 transition-colors">{feature.name}</h3>
                                                <p className="text-gray-600 leading-relaxed">{feature.desc}</p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };
// --- END MODIFIED FeaturesPage ---


// --- MODIFIED PricingPage WITH TOGGLE AND HOTEL PRICING ---
    const PricingPage = () => {
        const currentPackages = businessType === 'hotel' ? HOTEL_PACKAGES : packages;
        const typeTitle = businessType === 'hotel' ? 'Hotel Room Service Ordering' : 'Restaurant POS System';
        const limitKey = businessType === 'hotel' ? 'rooms' : 'qrTables';
        const limitText = businessType === 'hotel' ? 'Rooms' : 'QR Tables';

        return (
            <div className="pt-20 bg-gray-50 min-h-screen">
                <div className="bg-gradient-to-br from-orange-500 via-red-600 to-purple-600 text-white py-28 relative overflow-hidden">
                    <div className="absolute inset-0 opacity-20" style={{
                        backgroundImage: `radial-gradient(circle, rgba(255,255,255,0.3) 1px, transparent 1px)`,
                        backgroundSize: '50px 50px'
                    }}></div>
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
                        <div className="inline-block px-6 py-3 bg-white/20 backdrop-blur-lg rounded-full text-sm font-bold mb-6">
                            <DollarSign className="inline mr-2" size={16} /> TRANSPARENT PRICING
                        </div>
                        <h1 className="text-6xl md:text-7xl font-black mb-8">Simple Pricing for<br />{typeTitle}</h1>
                        <p className="text-2xl opacity-95 max-w-3xl mx-auto">Choose the perfect plan for your business needs</p>
                    </div>
                </div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
                    {/* Business Type Toggle (NEW) */}
                    <div className="flex justify-center mb-16">
                        <div className="inline-flex rounded-full bg-gray-200 p-2 shadow-lg">
                            <button
                                onClick={() => setBusinessType('restaurant')}
                                className={`px-8 py-3 rounded-full text-lg font-bold transition-all duration-300 flex items-center gap-2 ${
                                    businessType === 'restaurant'
                                        ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white shadow-xl'
                                        : 'text-gray-700 hover:bg-white'
                                }`}
                            >
                                🍽️ Restaurant Pricing
                            </button>
                            <button
                                onClick={() => setBusinessType('hotel')}
                                className={`px-8 py-3 rounded-full text-lg font-bold transition-all duration-300 flex items-center gap-2 ${
                                    businessType === 'hotel'
                                        ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white shadow-xl'
                                        : 'text-gray-700 hover:bg-white'
                                }`}
                            >
                                🏨 Hotel Pricing
                            </button>
                        </div>
                    </div>
                    {/* End Business Type Toggle */}
                    
                    <div className="grid md:grid-cols-3 gap-10">
                        {currentPackages.map((pkg, idx) => (
                            <div key={pkg.id} className={`relative rounded-3xl overflow-hidden transition-all duration-500 hover:scale-105 ${pkg.popular ? 'transform scale-105' : ''}`}>
                                {pkg.popular && (
        <div className="absolute top-1 -left-90 z-30 transform -rotate-0">
        <div className="bg-gradient-to-r from-yellow-400 via-orange-500 to-red-600 text-white px-6 py-2 rounded-full text-sm font-black shadow-2xl flex items-center gap-2 w-64 justify-center" style={{animation: 'pulse-glow 2s ease-in-out infinite'}}>
            <Star className="fill-current" size={16} /> MOST POPULAR <Star className="fill-current" size={16} />
        </div>
    </div>
)}
                                <div className={`bg-gradient-to-br ${pkg.popular ? 'from-orange-500 via-red-500 to-red-600' : 'from-gray-200 to-gray-300'} p-1 h-full`}>
                                    <div className="bg-white rounded-3xl p-10 h-full flex flex-col">
                                        <div className="text-center mb-8">
                                            <h3 className="text-4xl font-black mb-4 bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">{pkg.name}</h3>
                                            <div className="mb-6">
                                                <span className="text-6xl font-black bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">₹{pkg.price}</span>
                                                <span className="text-gray-600 text-xl">/month</span>
                                            </div>
                                            <div className="text-md text-gray-500 font-semibold italic">
                                                {limitKey === 'rooms' ? `Up to ${pkg[limitKey]} ${limitText}` : `Up to ${pkg[limitKey]} ${limitText}`}
                                            </div>
                                        </div>
                                        <ul className="space-y-4 mb-10 flex-grow">
                                            {pkg.features.map((feature, fIdx) => (
                                                <li key={fIdx} className="flex items-start gap-3 text-gray-700">
                                                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-r from-green-400 to-emerald-600 flex items-center justify-center mt-0.5">
                                                        <Check className="text-white" size={14} />
                                                    </div>
                                                    <span className="text-sm leading-relaxed">{feature}</span>
                                                </li>
                                            ))}
                                            <li className="flex items-start gap-3 text-orange-600 font-semibold pt-4 border-t border-gray-200">
                                                <Plus size={20} className="flex-shrink-0 mt-0.5" />
                                                <span className="text-sm">Mini Bill Printer: +₹{BILL_PRINTER_PRICE} One-Time</span>
                                            </li>
                                        </ul>
                                        <button
                                            onClick={() => setCurrentPage('contact')}
                                            className={`w-full py-5 rounded-2xl font-bold transition-all transform hover:scale-105 shadow-xl text-lg ${
                                                pkg.popular
                                                    ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white hover:shadow-orange-500/50'
                                                    : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                                            }`}
                                        >
                                            Get Started <ArrowRight className="inline ml-2" size={20} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="mt-20 bg-gradient-to-r from-yellow-500 via-orange-500 to-red-600 p-1 rounded-3xl">
                        <div className="bg-white rounded-3xl p-12 text-center">
                            <div className="text-6xl mb-6">🎉</div>
                            <h3 className="text-4xl font-black mb-4 bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">Special Launch Offer!</h3>
                            <p className="text-xl text-gray-700 mb-4">Get <span className="font-bold text-orange-600">1 Month FREE</span> on any annual plan + Free setup & training!</p>
                            <p className="text-sm text-gray-600">* All prices exclusive of GST. No hidden charges.</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    };
// --- END MODIFIED PricingPage ---

    const ContactPage = () => {
        const [formData, setFormData] = useState({
            name: '',
            email: '',
            phone: '',
            restaurant: '',
            message: '',
            package: 'Premium (restaurant)' // Default package updated to include type
        });
        const [billPrinterAddOn, setBillPrinterAddOn] = useState(false);

        const handleInputChange = (e) => {
            const { name, value } = e.target;
            setFormData(prev => ({ ...prev, [name]: value }));
        };

        const handleSubmit = async (e) => {
            e.preventDefault();
            
            const dataToSubmit = {
                ...formData,
                billPrinterAddOn: billPrinterAddOn ? `Yes, +₹${BILL_PRINTER_PRICE} one-time` : 'No',
                totalEstimatedCost: billPrinterAddOn
                    ? `${formData.package} Package Price + ₹${BILL_PRINTER_PRICE} setup`
                    : `${formData.package} Package Price`,
                businessType: formData.package.includes('(hotel)') ? 'hotel' : 'restaurant' // Infer type from selected package
            };
            
            setTimeout(async () => {
                const success = await submitDemoRequest(dataToSubmit);
                if (success) {
                    setFormData({
                        name: '',
                        email: '',
                        phone: '',
                        restaurant: '',
                        message: '',
                        package: 'Premium (restaurant)'
                    });
                    setBillPrinterAddOn(false);
                }
            }, 0);
        };

        return (
            <div className="pt-20 bg-gradient-to-br from-gray-50 to-orange-50 min-h-screen">
                <div className="bg-gradient-to-br from-orange-500 via-red-600 to-purple-600 text-white py-28 relative overflow-hidden">
                    <div className="absolute inset-0 opacity-20" style={{
                        backgroundImage: `radial-gradient(circle at 30% 50%, rgba(255,255,255,0.3) 0%, transparent 50%)`
                    }}></div>
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
                        <div className="inline-block px-6 py-3 bg-white/20 backdrop-blur-lg rounded-full text-sm font-bold mb-6">
                            <Mail className="inline mr-2" size={16} /> GET IN TOUCH
                        </div>
                        <h1 className="text-6xl md:text-7xl font-black mb-8">Let's Connect</h1>
                        <p className="text-2xl opacity-95 max-w-3xl mx-auto">We're here to answer all your questions and help you get started</p>
                    </div>
                </div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
                    <div className="grid md:grid-cols-2 gap-12">
                        <div className="bg-white rounded-3xl shadow-2xl p-10 hover:shadow-orange-200 transition-all duration-500 border-2 border-orange-100">
                            <h2 className="text-4xl font-black bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent mb-8">Request a Free Demo</h2>
                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-3">Full Name *</label>
                                    <input
                                        type="text"
                                        name="name"
                                        required
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        className="w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all hover:border-orange-300 text-lg"
                                        placeholder="Enter your name"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-3">Email *</label>
                                    <input
                                        type="email"
                                        name="email"
                                        required
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        className="w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all hover:border-orange-300 text-lg"
                                        placeholder="your@email.com"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-3">Phone Number *</label>
                                    <input
                                        type="tel"
                                        name="phone"
                                        required
                                        value={formData.phone}
                                        onChange={handleInputChange}
                                        className="w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all hover:border-orange-300 text-lg"
                                        placeholder="+91 XXXXX XXXXX"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-3">Restaurant/Hotel Name *</label>
                                    <input
                                        type="text"
                                        name="restaurant"
                                        required
                                        value={formData.restaurant}
                                        onChange={handleInputChange}
                                        className="w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all hover:border-orange-300 text-lg"
                                        placeholder="Your business name"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-3">Interested Package</label>
                                    <select
                                        name="package"
                                        value={formData.package}
                                        onChange={handleInputChange}
                                        className="w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all hover:border-orange-300 text-lg"
                                    >
                                        {[...DEFAULT_PACKAGES, ...HOTEL_PACKAGES].map(pkg => (
                                            <option key={pkg.id} value={`${pkg.name} (${pkg.type})`}>{pkg.name} ({pkg.type}) - ₹{pkg.price}/month</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="pt-2">
                                    <h3 className="text-lg font-black text-gray-900 mb-4 flex items-center gap-2">
                                        <Zap className="text-orange-500" size={22} /> Optional Add-ons
                                    </h3>
                                    <label className="flex items-center space-x-4 p-5 bg-gradient-to-r from-orange-50 to-red-50 rounded-xl border-2 border-orange-200 cursor-pointer hover:border-orange-400 hover:shadow-lg transition-all">
                                        <input
                                            type="checkbox"
                                            checked={billPrinterAddOn}
                                            onChange={(e) => setBillPrinterAddOn(e.target.checked)}
                                            className="form-checkbox h-6 w-6 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                                        />
                                        <span className="text-gray-900 font-bold flex-1 text-lg">
                                            Mini Bill Printer (Thermal)
                                        </span>
                                        <span className="text-orange-600 font-black flex-shrink-0 text-lg">
                                            + ₹{BILL_PRINTER_PRICE}
                                        </span>
                                    </label>
                                    {billPrinterAddOn && (
                                        <p className="text-sm text-gray-600 mt-3 ml-10 italic">
                                            This cost covers the hardware and one-time setup charge.
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-3">Message</label>
                                    <textarea
                                        name="message"
                                        value={formData.message}
                                        onChange={handleInputChange}
                                        rows={4}
                                        className="w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all hover:border-orange-300 text-lg"
                                        placeholder="Tell us about your requirements..."
                                    />
                                </div>

                                <button
                                    type="submit"
                                    className="w-full bg-gradient-to-r from-orange-500 via-red-500 to-red-600 text-white py-5 rounded-xl font-black text-xl hover:shadow-2xl transition-all transform hover:scale-105 flex items-center justify-center gap-3 group relative overflow-hidden"
                                >
                                    <div className="absolute inset-0 bg-gradient-to-r from-orange-400 to-red-500 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                                    <span className="relative z-10">Request Demo</span>
                                    <ArrowRight className="relative z-10 group-hover:translate-x-2 transition-transform" size={24} />
                                </button>
                            </form>
                        </div>

                        <div className="space-y-8">
                            <div className="bg-white rounded-3xl shadow-2xl p-10 hover:shadow-orange-200 transition-all duration-500 border-2 border-orange-100">
                                <h2 className="text-4xl font-black bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent mb-8">Contact Information</h2>
                                <div className="space-y-6">
                                    <div className="flex items-start gap-5 hover:bg-orange-50 p-4 rounded-xl transition-all group">
                                        <div className="p-3 bg-gradient-to-r from-orange-500 to-red-600 rounded-xl group-hover:scale-110 transition-transform">
                                            <Phone className="text-white" size={24} />
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-gray-900 mb-2 text-lg">Phone / WhatsApp</h4>
                                            <p className="text-gray-600 text-lg">+91-8178903697</p>
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-5 hover:bg-orange-50 p-4 rounded-xl transition-all group">
                                        <div className="p-3 bg-gradient-to-r from-orange-500 to-red-600 rounded-xl group-hover:scale-110 transition-transform">
                                            <Mail className="text-white" size={24} />
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-gray-900 mb-2 text-lg">Email</h4>
                                            <p className="text-gray-600 text-lg">contact@zyppro.com</p>
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-5 hover:bg-orange-50 p-4 rounded-xl transition-all group">
                                        <div className="p-3 bg-gradient-to-r from-orange-500 to-red-600 rounded-xl group-hover:scale-110 transition-transform">
                                            <Globe className="text-white" size={24} />
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-gray-900 mb-2 text-lg">Website</h4>
                                            <p className="text-gray-600 text-lg">www.zyppro.com</p>
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-5 hover:bg-orange-50 p-4 rounded-xl transition-all group">
                                        <div className="p-3 bg-gradient-to-r from-orange-500 to-red-600 rounded-xl group-hover:scale-110 transition-transform">
                                            <MapPin className="text-white" size={24} />
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-gray-900 mb-2 text-lg">Address</h4>
                                            <p className="text-gray-600 text-lg">Delhi, India</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-gradient-to-br from-orange-500 via-red-500 to-red-600 text-white rounded-3xl p-10 hover:scale-105 transition-all duration-500 shadow-2xl">
                                <h3 className="text-3xl font-black mb-6">Get Started in 3 Steps</h3>
                                <div className="space-y-6">
                                    <div className="flex items-start gap-4">
                                        <div className="bg-white text-orange-600 w-12 h-12 rounded-full flex items-center justify-center font-black text-xl flex-shrink-0 shadow-lg">1</div>
                                        <p className="opacity-95 text-lg pt-2">Request a free personalized demo</p>
                                    </div>
                                    <div className="flex items-start gap-4">
                                        <div className="bg-white text-orange-600 w-12 h-12 rounded-full flex items-center justify-center font-black text-xl flex-shrink-0 shadow-lg">2</div>
                                        <p className="opacity-95 text-lg pt-2">We set up everything within 24 hours</p>
                                    </div>
                                    <div className="flex items-start gap-4">
                                        <div className="bg-white text-orange-600 w-12 h-12 rounded-full flex items-center justify-center font-black text-xl flex-shrink-0 shadow-lg">3</div>
                                        <p className="opacity-95 text-lg pt-2">Get training and go live!</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

// --- CORRECTED ADMIN PANEL COMPONENT DEFINITION ---
    const AdminPanel = ({
        setAdminLoggedIn, setAdminUser, handleAdminLogout,
        demoRequests, updateRequestStatus, deleteRequest, packages,
        editingPackage, setEditingPackage, updatePackage, deletePackage,
        showAddPackage, setShowAddPackage, newPackage, setNewPackage, addNewPackage,
        businessType, setBusinessType
    }) => {

        if (!auth.currentUser) {
            return (
                <div className="pt-20 min-h-screen bg-gradient-to-br from-orange-100 via-red-100 to-purple-100 flex items-center justify-center p-4">
                    <AdminLoginForm
                        setAdminLoggedIn={setAdminLoggedIn}
                        setAdminUser={setAdminUser}
                    />
                </div>
            );
        }

        return (
            <div className="pt-20 bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                    <div className="flex justify-between items-center mb-12 bg-white rounded-2xl p-8 shadow-xl border-2 border-orange-100">
                        <div>
                            <h1 className="text-5xl font-black bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">Admin Dashboard</h1>
                            <p className="text-gray-600 mt-3 text-lg">Manage your packages and demo requests</p>
                            <p className="text-sm text-gray-500 mt-2 flex items-center gap-2">
                                <Shield size={16} /> Logged in as: <span className="font-semibold">{auth.currentUser.email}</span>
                            </p>
                        </div>
                        <button
                            onClick={handleAdminLogout}
                            className="bg-gradient-to-r from-red-500 to-red-600 text-white px-8 py-4 rounded-xl hover:shadow-2xl transition-all transform hover:scale-105 font-bold flex items-center gap-2"
                        >
                            <Lock size={20} /> Logout
                        </button>
                    </div>

                    {/* Demo Requests Section */}
                    <div className="bg-white rounded-2xl shadow-2xl p-8 mb-12 border-2 border-orange-100">
                        <h2 className="text-3xl font-black text-gray-900 mb-8 flex items-center gap-3">
                            <div className="p-3 bg-gradient-to-r from-orange-500 to-red-600 rounded-xl">
                                <Mail className="text-white" size={24} />
                            </div>
                            Demo Requests
                            <span className="ml-2 px-4 py-1 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-full text-xl">{demoRequests.length}</span>
                        </h2>
                        <div className="overflow-x-auto rounded-xl border-2 border-gray-200">
                            <table className="min-w-full">
                                <thead className="bg-gradient-to-r from-orange-500 to-red-600 text-white">
                                    <tr>
                                        <th className="px-6 py-4 text-left text-sm font-bold">Name</th>
                                        <th className="px-6 py-4 text-left text-sm font-bold">Restaurant</th>
                                        <th className="px-6 py-4 text-left text-sm font-bold">Email</th>
                                        <th className="px-6 py-4 text-left text-sm font-bold">Phone</th>
                                        <th className="px-6 py-4 text-left text-sm font-bold">Package</th>
                                        <th className="px-6 py-4 text-left text-sm font-bold">Printer</th>
                                        <th className="px-6 py-4 text-left text-sm font-bold">Date</th>
                                        <th className="px-6 py-4 text-left text-sm font-bold">Status</th>
                                        <th className="px-6 py-4 text-left text-sm font-bold">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {demoRequests.map((request, idx) => (
                                        <tr key={idx} className="hover:bg-orange-50 transition-all">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-semibold">{request.name}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{request.restaurant}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{request.email}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{request.phone}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 font-semibold">{request.package}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-bold">
                                                {request.billPrinterAddOn && request.billPrinterAddOn.startsWith('Yes') ? (
                                                     <span className="text-green-600 flex items-center gap-1"><CheckCircle size={16} /> Yes</span>
                                                ) : (
                                                     <span className="text-red-500">No</span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{new Date(request.timestamp).toLocaleDateString()}</td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <select
                                                    value={request.status}
                                                    onChange={(e) => updateRequestStatus(request.id, e.target.value)}
                                                    className="text-sm border-2 border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 font-semibold"
                                                >
                                                    <option value="pending">Pending</option>
                                                    <option value="contacted">Contacted</option>
                                                    <option value="completed">Completed</option>
                                                </select>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <button
                                                    onClick={() => deleteRequest(request.id)}
                                                    className="text-red-500 hover:text-red-700 transition-all hover:scale-110 p-2 hover:bg-red-50 rounded-lg"
                                                >
                                                    <Trash2 size={20} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {demoRequests.length === 0 && (
                                <div className="text-center py-16 text-gray-500">
                                    <Mail size={64} className="mx-auto mb-4 opacity-30" />
                                    <p className="text-xl font-semibold">No demo requests yet</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Package Management Section */}
                    <div className="bg-white rounded-2xl shadow-2xl p-8 border-2 border-orange-100">
                        <div className="flex justify-between items-center mb-8 flex-wrap gap-4">
                            <h2 className="text-3xl font-black text-gray-900 flex items-center gap-3">
                                <div className="p-3 bg-gradient-to-r from-orange-500 to-red-600 rounded-xl">
                                    <DollarSign className="text-white" size={24} />
                                </div>
                                Manage Packages ({businessType.charAt(0).toUpperCase() + businessType.slice(1)})
                                <span className="ml-2 px-4 py-1 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-full text-xl">{packages.filter(p => p.type === businessType).length}</span>
                            </h2>
                            <div className="flex items-center space-x-3">
                                <div className="inline-flex rounded-full bg-gray-200 p-1 shadow-inner">
                                    <button
                                        onClick={() => setBusinessType('restaurant')}
                                        className={`px-4 py-2 rounded-full text-sm font-bold transition-all duration-300 ${
                                            businessType === 'restaurant'
                                                ? 'bg-white text-orange-600 shadow-md'
                                                : 'text-gray-700'
                                        }`}
                                    >
                                        🍽️ Restaurant
                                    </button>
                                    <button
                                        onClick={() => setBusinessType('hotel')}
                                        className={`px-4 py-2 rounded-full text-sm font-bold transition-all duration-300 ${
                                            businessType === 'hotel'
                                                ? 'bg-white text-orange-600 shadow-md'
                                                : 'text-gray-700'
                                        }`}
                                    >
                                        🏨 Hotel
                                    </button>
                                </div>
                                <button
                                    onClick={() => setShowAddPackage(true)}
                                    className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-6 py-3 rounded-xl font-bold hover:shadow-lg transition-all transform hover:scale-105 flex items-center gap-2"
                                >
                                    <Plus size={20} /> Add Package
                                </button>
                            </div>
                        </div>

                        {showAddPackage && (
                            <div className="mb-10 p-8 bg-gradient-to-r from-orange-50 to-red-50 rounded-2xl border-2 border-orange-300">
                                <h3 className="text-2xl font-black text-gray-900 mb-6">Add New Package (Type: {businessType})</h3>
                                <div className="grid md:grid-cols-3 gap-6 mb-6">
                                    <input
                                        type="text"
                                        value={newPackage.name}
                                        onChange={(e) => setNewPackage({...newPackage, name: e.target.value})}
                                        className="px-5 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 font-semibold"
                                        placeholder="Package Name"
                                    />
                                    <input
                                        type="number"
                                        value={newPackage.price}
                                        onChange={(e) => setNewPackage({...newPackage, price: e.target.value})}
                                        className="px-5 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 font-semibold"
                                        placeholder="Price (₹)"
                                    />
                                    <input
                                        type="number"
                                        value={newPackage.qrTables}
                                        onChange={(e) => setNewPackage({...newPackage, qrTables: e.target.value})}
                                        className="px-5 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 font-semibold"
                                        placeholder={businessType === 'hotel' ? "Max Rooms" : "Max QR Tables"}
                                    />
                                </div>
                                <div className="mb-6">
                                    <label className="block text-sm font-bold text-gray-700 mb-3">Features</label>
                                    {newPackage.features.map((feature, idx) => (
                                        <div key={idx} className="flex gap-3 mb-3 items-center">
                                            <input
                                                type="text"
                                                value={feature}
                                                onChange={(e) => {
                                                    const newFeatures = [...newPackage.features];
                                                    newFeatures[idx] = e.target.value;
                                                    setNewPackage({...newPackage, features: newFeatures});
                                                }}
                                                className="flex-1 px-5 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500"
                                                placeholder="Feature description"
                                            />
                                            {idx === newPackage.features.length - 1 && (
                                                <button
                                                    onClick={() => setNewPackage({...newPackage, features: [...newPackage.features, '']})}
                                                    className="bg-green-500 text-white px-5 py-3 rounded-xl hover:bg-green-600 font-bold flex-shrink-0"
                                                >
                                                    <Plus size={20} />
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                                <div className="flex gap-3">
                                    <button
                                        onClick={addNewPackage}
                                        className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-8 py-3 rounded-xl font-bold hover:shadow-lg transition-all"
                                    >
                                        Save Package
                                    </button>
                                    <button
                                        onClick={() => {
                                            setShowAddPackage(false);
                                            setNewPackage({ name: '', price: '', qrTables: '', features: [''] });
                                        }}
                                        className="bg-gray-500 text-white px-8 py-3 rounded-xl font-bold hover:bg-gray-600 transition-all"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        )}

                        <div className="grid md:grid-cols-3 gap-8">
                            {packages.filter(p => p.type === businessType).map((pkg) => (
                                <div key={pkg.id} className={`border-2 rounded-2xl p-8 transition-all hover:shadow-2xl ${pkg.popular ? 'border-orange-500 bg-orange-50' : 'border-gray-200'}`}>
                                    {editingPackage === pkg.id ? (
                                        <div className="space-y-4">
                                            <input
                                                type="text"
                                                value={pkg.name}
                                                onChange={(e) => {
                                                    const newPackages = packages.map(p =>
                                                        p.id === pkg.id ? {...p, name: e.target.value} : p
                                                    );
                                                    setPackages(newPackages);
                                                }}
                                                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 font-bold"
                                                placeholder="Package Name"
                                            />
                                            <input
                                                type="number"
                                                value={pkg.price}
                                                onChange={(e) => {
                                                    const newPackages = packages.map(p =>
                                                        p.id === pkg.id ? {...p, price: parseInt(e.target.value) || 0} : p
                                                    );
                                                    setPackages(newPackages);
                                                }}
                                                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 font-bold"
                                                placeholder="Price"
                                            />
                                            <input
                                                type="number"
                                                value={pkg.qrTables}
                                                onChange={(e) => {
                                                    const newPackages = packages.map(p =>
                                                        p.id === pkg.id ? {...p, qrTables: parseInt(e.target.value) || 0} : p
                                                    );
                                                    setPackages(newPackages);
                                                }}
                                                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 font-bold"
                                                placeholder={businessType === 'hotel' ? "Max Rooms" : "Max QR Tables"}
                                            />
                                            {/* Feature editing needs a more complex UI, simplifying for save/cancel functionality demonstration */}
                                            <div className="bg-gray-100 p-3 rounded-lg text-sm text-gray-700 italic">
                                                Features editing UI omitted for brevity. Edit features in-line within the 'new package' form.
                                            </div>

                                            <div className="flex gap-3 pt-2">
                                                <button
                                                    onClick={() => {
                                                        updatePackage(pkg.id, pkg);
                                                        setEditingPackage(null);
                                                    }}
                                                    className="flex-1 bg-green-500 text-white px-5 py-3 rounded-xl hover:bg-green-600 font-bold transition-all"
                                                >
                                                    Save
                                                </button>
                                                <button
                                                    onClick={() => setEditingPackage(null)}
                                                    className="flex-1 bg-gray-500 text-white px-5 py-3 rounded-xl hover:bg-gray-600 font-bold transition-all"
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div>
                                            {pkg.popular && (
                                                <div className="bg-orange-500 text-white text-xs font-black px-3 py-1 rounded-full inline-block mb-3">
                                                    POPULAR
                                                </div>
                                            )}
                                            <h3 className="text-3xl font-black text-gray-900 mb-3">{pkg.name}</h3>
                                            <div className="text-4xl font-black bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent mb-2">₹{pkg.price}</div>
                                            <div className="text-sm text-gray-600 mb-6 font-semibold">
                                                {businessType === 'hotel' ? `Up to ${pkg.rooms} Rooms` : `Up to ${pkg.qrTables} QR Tables`}
                                            </div>
                                            <div className="flex gap-3">
                                                <button
                                                    onClick={() => setEditingPackage(pkg.id)}
                                                    className="flex-1 bg-orange-500 text-white px-5 py-3 rounded-xl hover:bg-orange-600 font-bold transition-all flex items-center justify-center gap-2"
                                                >
                                                    <Edit size={18} /> Edit
                                                </button>
                                                <button
                                                    onClick={() => deletePackage(pkg.id)}
                                                    className="bg-red-500 text-white px-5 py-3 rounded-xl hover:bg-red-600 transition-all"
                                                >
                                                    <Trash2 size={18} />
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    };
// --- END CORRECTED ADMIN PANEL COMPONENT DEFINITION ---

    const PrivacyPolicyPage = () => (
        <div className="pt-20 bg-white min-h-screen">
            <div className="bg-gradient-to-br from-orange-500 to-red-600 text-white py-20">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-center mb-6">
                        <Lock className="text-yellow-300" size={60} />
                    </div>
                    <h1 className="text-5xl md:text-6xl font-black mb-4 text-center">Privacy Policy</h1>
                    <p className="text-xl opacity-90 text-center">Last updated: {new Date().toLocaleDateString()}</p>
                </div>
            </div>

            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                <div className="prose prose-lg max-w-none">
                    <div className="bg-blue-50 border-l-4 border-blue-500 p-8 mb-10 rounded-r-2xl">
                        <p className="text-gray-700 leading-relaxed text-lg">
                            At Zyppro RMS, we are committed to protecting your privacy. This Privacy Policy explains how we collect, use, and safeguard your information.
                        </p>
                    </div>

                    <section className="mb-10 bg-white p-8 rounded-2xl shadow-lg border-2 border-gray-100">
                        <h2 className="text-3xl font-black text-gray-900 mb-6 flex items-center gap-3">
                            <Lock className="text-orange-500" size={28} /> Information We Collect
                        </h2>
                        <p className="text-gray-700 mb-4 text-lg">We collect information that you provide directly to us, including:</p>
                        <ul className="list-disc list-inside space-y-3 text-gray-700 ml-4 text-lg">
                            <li>Name, email address, and phone number</li>
                            <li>Restaurant name and business information</li>
                            <li>Menu items, prices, and descriptions</li>
                            <li>Order and transaction data</li>
                            <li>Usage data and analytics</li>
                        </ul>
                    </section>

                    <section className="mb-10 bg-white p-8 rounded-2xl shadow-lg border-2 border-gray-100">
                        <h2 className="text-3xl font-black text-gray-900 mb-6">How We Use Your Information</h2>
                        <ul className="list-disc list-inside space-y-3 text-gray-700 ml-4 text-lg">
                            <li>To provide and maintain our restaurant management services</li>
                            <li>To process orders and generate bills</li>
                            <li>To send you technical notices and support messages</li>
                            <li>To analyze usage and improve our services</li>
                            <li>To detect and prevent fraud</li>
                        </ul>
                    </section>

                    <section className="mb-10 bg-white p-8 rounded-2xl shadow-lg border-2 border-gray-100">
                        <h2 className="text-3xl font-black text-gray-900 mb-6">Data Security</h2>
                        <p className="text-gray-700 mb-4 text-lg">
                            We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. All data is encrypted in transit and at rest.
                        </p>
                    </section>

                    <section className="mb-10 bg-gradient-to-r from-orange-50 to-red-50 p-8 rounded-2xl border-2 border-orange-200">
                        <h2 className="text-3xl font-black text-gray-900 mb-6">Contact Us</h2>
                        <p className="text-gray-700 text-lg">
                            If you have any questions about this Privacy Policy, please contact us at <span className="font-bold text-orange-600">contact@zyppro.com</span>
                        </p>
                    </section>
                </div>
            </div>
        </div>
    );

    const TermsPage = () => (
        <div className="pt-20 bg-white min-h-screen">
            <div className="bg-gradient-to-br from-orange-500 to-red-600 text-white py-20">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-center mb-6">
                        <FileText className="text-yellow-300" size={60} />
                    </div>
                    <h1 className="text-5xl md:text-6xl font-black mb-4 text-center">Terms and Conditions</h1>
                    <p className="text-xl opacity-90 text-center">Last updated: {new Date().toLocaleDateString()}</p>
                </div>
            </div>

            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                <div className="prose prose-lg max-w-none">
                    <div className="bg-orange-50 border-l-4 border-orange-500 p-8 mb-10 rounded-r-2xl">
                        <p className="text-gray-700 leading-relaxed text-lg">
                            Please read these Terms and Conditions carefully before using Zyppro RMS services.
                        </p>
                    </div>

                    <section className="mb-10 bg-white p-8 rounded-2xl shadow-lg border-2 border-gray-100">
                        <h2 className="text-3xl font-black text-gray-900 mb-6">Service Description</h2>
                        <p className="text-gray-700 mb-4 text-lg">
                            Zyppro RMS is a cloud-based restaurant management system that provides QR-based ordering, menu management, billing, analytics, and related services.
                        </p>
                    </section>

                    <section className="mb-10 bg-white p-8 rounded-2xl shadow-lg border-2 border-gray-100">
                        <h2 className="text-3xl font-black text-gray-900 mb-6">User Responsibilities</h2>
                        <p className="text-gray-700 mb-4 text-lg">
                            Users are responsible for maintaining the confidentiality of their account credentials and for all activities that occur under their account.
                        </p>
                    </section>

                    <section className="mb-10 bg-white p-8 rounded-2xl shadow-lg border-2 border-gray-100">
                        <h2 className="text-3xl font-black text-gray-900 mb-6">Payment Terms</h2>
                        <p className="text-gray-700 mb-4 text-lg">
                            All prices are in Indian Rupees (INR) and exclusive of GST. Payment is required monthly in advance unless otherwise agreed.
                        </p>
                    </section>

                    <section className="mb-10 bg-gradient-to-r from-orange-50 to-red-50 p-8 rounded-2xl border-2 border-orange-200">
                        <h2 className="text-3xl font-black text-gray-900 mb-6">Contact Information</h2>
                        <p className="text-gray-700 text-lg">
                            For questions about these Terms: <span className="font-bold text-orange-600">contact@zyppro.com</span>
                        </p>
                    </section>
                </div>
            </div>
        </div>
    );

    
    const Footer = () => (
        <footer className="bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white py-16 relative overflow-hidden">
            <div className="absolute inset-0 opacity-5" style={{
                backgroundImage: `radial-gradient(circle at 50% 50%, #f97316 2px, transparent 2px)`,
                backgroundSize: '50px 50px'
            }}></div>
            
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                <div className="grid md:grid-cols-4 gap-12 mb-12">
                    <div>
                        <h3 className="text-3xl font-black mb-4 bg-gradient-to-r from-orange-400 to-red-500 bg-clip-text text-transparent">🍽️ Zyppro RMS</h3>
                        <p className="text-gray-400 text-lg">Restaurant Management System</p>
                    </div>
                    <div>
                        <h4 className="font-black mb-6 text-xl text-orange-400">Quick Links</h4>
                        <div className="space-y-3">
                            <button onClick={() => setCurrentPage('home')} className="block text-gray-400 hover:text-orange-400 transition-all hover:translate-x-2 duration-300 text-lg">→ Home</button>
                            <button onClick={() => setCurrentPage('features')} className="block text-gray-400 hover:text-orange-400 transition-all hover:translate-x-2 duration-300 text-lg">→ Features</button>
                            <button onClick={() => setCurrentPage('pricing')} className="block text-gray-400 hover:text-orange-400 transition-all hover:translate-x-2 duration-300 text-lg">→ Pricing</button>
                            <button onClick={() => setCurrentPage('contact')} className="block text-gray-400 hover:text-orange-400 transition-all hover:translate-x-2 duration-300 text-lg">→ Contact</button>
                        </div>
                    </div>
                    <div>
                        <h4 className="font-black mb-6 text-xl text-orange-400">Legal</h4>
                        <div className="space-y-3">
                            <button onClick={() => setCurrentPage('privacy')} className="block text-gray-400 hover:text-orange-400 transition-all hover:translate-x-2 duration-300 text-lg">→ Privacy Policy</button>
                            <button onClick={() => setCurrentPage('terms')} className="block text-gray-400 hover:text-orange-400 transition-all hover:translate-x-2 duration-300 text-lg">→ Terms & Conditions</button>
                        </div>
                    </div>
                    <div>
                        <h4 className="font-black mb-6 text-xl text-orange-400">Contact</h4>
                        <div className="space-y-4 text-gray-400 text-lg">
                            <p className="flex items-center gap-2">📱 +91-8178903697</p>
                            <p className="flex items-center gap-2">📧 contact@zyppro.com</p>
                            <p className="flex items-center gap-2">📍 Delhi, India</p>
                        </div>
                    </div>
                </div>
                
                <div className="border-t border-gray-800 pt-8">
                    <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                        <p className="text-gray-400 text-center md:text-left">&copy; {new Date().getFullYear()} Zyppro RMS. All rights reserved.</p>
                        <button
                            onClick={() => setCurrentPage('admin-login')}
                            className="text-xs text-gray-600 hover:text-orange-400 transition-all px-4 py-2 rounded-lg hover:bg-gray-800"
                        >
                            <Lock size={14} className="inline mr-1" /> Admin Access
                        </button>
                    </div>
                </div>
            </div>
        </footer>
    );

    return (
        <div className="min-h-screen bg-white">
            <style>{`
                @keyframes float {
                    0%, 100% { transform: translateY(0px) rotate(0deg); }
                    50% { transform: translateY(-20px) rotate(5deg); }
                }
                @keyframes pulse-glow {
                    0%, 100% { box-shadow: 0 0 40px rgba(251, 146, 60, 0.4); }
                    50% { box-shadow: 0 0 80px rgba(251, 146, 60, 0.6); }
                }
                .float-animation {
                    animation: float 6s ease-in-out infinite;
                }
                .gradient-text {
                    background: linear-gradient(135deg, #f97316 0%, #dc2626 100%);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    background-clip: text;
                }
                * {
                    scroll-behavior: smooth;
                }
            `}</style>
            
            <Navigation />
            
            {currentPage === 'home' && <HomePage />}
            {currentPage === 'features' && <FeaturesPage />}
            {currentPage === 'pricing' && <PricingPage />}
            {currentPage === 'contact' && <ContactPage />}
            {currentPage === 'privacy' && <PrivacyPolicyPage />}
            {currentPage === 'terms' && <TermsPage />}
            
            {/* --- CORRECTED ADMINPANEL CALL --- */}
            {currentPage === 'admin-login' && (
                <AdminPanel
                    setAdminLoggedIn={setAdminLoggedIn}
                    setAdminUser={setAdminUser}
                    adminUser={adminUser}
                    handleAdminLogout={handleAdminLogout}
                    demoRequests={demoRequests}
                    updateRequestStatus={updateRequestStatus}
                    deleteRequest={deleteRequest}
                    packages={packages}
                    editingPackage={editingPackage}
                    setEditingPackage={setEditingPackage}
                    updatePackage={updatePackage}
                    deletePackage={deletePackage}
                    showAddPackage={showAddPackage}
                    setShowAddPackage={setShowAddPackage}
                    newPackage={newPackage}
                    setNewPackage={setNewPackage}
                    addNewPackage={addNewPackage}
                    businessType={businessType}
                    setBusinessType={setBusinessType} // Pass the setter for the toggle in AdminPanel
                />
            )}
            <ChatBot />
            {/* --------------------------------- */}

            <Footer />
        </div>
    );
};

export default ZypproWebsite;